import bookingItemTypes from 'enums/bookingItemTypes';
import paymentRecurrences from 'enums/paymentRecurrences';
import purchaseItemTypes from 'enums/purchaseItemTypes';
import { formatShortDate, calculateDaysInPeriod, daysPerMonth } from 'helpers/DateHelper';
import { calculateSum } from 'helpers/ArrayHelper';
import { roundAmount, calculateVat } from 'helpers/MonetaryHelper';
import { getTimePeriodPriceOverlap, timePeriodPriceIsActive } from 'logic/timePeriodPriceLogic';
import strings from 'localization/strings';

export const createSubscriptionBookingRecurringPurchaseItems = (bookingItems, timePeriod, monthFactor, appContext) => {
    const purchaseItems = [];

    const perMonthBookingItems = bookingItems
        .filter(o => o.paymentRecurrence === paymentRecurrences.perMonth.key &&
                     o.type !== bookingItemTypes.requestedService.key);

    perMonthBookingItems.forEach(perMonthBookingItem => {
        const overlapInfos = perMonthBookingItem.timePeriodPrices
            .map(tpp => getTimePeriodPriceOverlap(tpp, timePeriod, appContext))
            .filter(o => o.overlaps);

        const totalNumberOfDaysForOverlappingTimePeriods = calculateSum(overlapInfos, o => o.overlappingTimePeriod.numberOfDays);

        const monthFactorIsInteger = monthFactor !== undefined && Math.abs(monthFactor - Math.round(monthFactor)) < 0.001;

        if (monthFactorIsInteger && totalNumberOfDaysForOverlappingTimePeriods === calculateDaysInPeriod(timePeriod)) {
            // special case when time period length is an even number of months, and the whole time period is covered by time period price(s)
            // distribute time period prices proportionally over this time period, regardless of number of days in month(s)
            overlapInfos.forEach(overlapInfo => {
                purchaseItems.push(createSubscriptionBookingPerMonthPurchaseItem(
                    perMonthBookingItem,
                    overlapInfo.timePeriodPrice,
                    overlapInfo.overlappingTimePeriod,
                    monthFactor * overlapInfo.overlappingTimePeriod.numberOfDays / totalNumberOfDaysForOverlappingTimePeriods,
                    appContext));
            });
        } else {
            overlapInfos.forEach(overlapInfo => {
                purchaseItems.push(createSubscriptionBookingPerMonthPurchaseItem(
                    perMonthBookingItem,
                    overlapInfo.timePeriodPrice,
                    overlapInfo.overlappingTimePeriod,
                    overlapInfo.overlappingTimePeriod.numberOfDays / daysPerMonth,
                    appContext));
            });
        }
    });

    return purchaseItems;
};

export const createOneTimePurchaseItems = (bookingItems, date, appContext) => {
    const purchaseItems = [];
    const oneTimeBookingItems = bookingItems
        .filter(o => o.paymentRecurrence === paymentRecurrences.oneTime.key &&
                    o.type !== bookingItemTypes.requestedService.key);
    oneTimeBookingItems.forEach(oneTimeBookingItem => {
        oneTimeBookingItem.timePeriodPrices
            .filter(o => timePeriodPriceIsActive(o, date, appContext))
            .forEach(timePeriodPrice => {
                purchaseItems.push(createPurchaseItem(oneTimeBookingItem, timePeriodPrice, timePeriodPrice.amount, oneTimeBookingItem.description, undefined, undefined, appContext));
            });
    });

    return purchaseItems;
};

export const createPeriodBookingPurchaseItems = (bookingItems, timePeriod, appContext) => {
    const purchaseItems = [];

    const perMonthBookingItems = bookingItems
        .filter(o => o.paymentRecurrence === paymentRecurrences.perMonth.key &&
                    o.type !== bookingItemTypes.requestedService);

    perMonthBookingItems.forEach(perMonthBookingItem => {
        const overlapInfos = perMonthBookingItem.timePeriodPrices
            .map(tpp => getTimePeriodPriceOverlap(tpp, timePeriod, appContext))
            .filter(o => o.overlaps);

        overlapInfos.forEach(overlapInfo => {
            const monthFactor = overlapInfo.overlappingTimePeriod.numberOfDays / daysPerMonth;
            const amount = roundAmount(monthFactor * overlapInfo.timePeriodPrice.amount, appContext);

            purchaseItems.push(createPurchaseItem(
                perMonthBookingItem,
                overlapInfo.timePeriodPrice,
                amount,
                perMonthBookingItem.description,
                overlapInfo.overlappingTimePeriod.startDate,
                overlapInfo.overlappingTimePeriod.endDate,
                appContext
            ));
        });
    });

    createOneTimePurchaseItems(bookingItems, timePeriod.startDate, appContext).forEach(purchaseItem => {
        purchaseItems.push(purchaseItem);
    });

    return purchaseItems;
};

export const createRoundingCorrectionPurchaseItem = (roundingCorrection, appContext) => {
    if ((roundingCorrection ?? 0) === 0) {
        return undefined;
    }

    return {
        description: strings.roundingCorrection,
        amount: roundingCorrection,
        currency: appContext.currency.code,
        vat: 0,
        vatRate: 0,
        commissionRate: 1,
        type: purchaseItemTypes.roundingCorrection.key
    };
};

export const formatPurchaseItemDescription = (purchaseItem, appContext) => {
    const timePeriodSpecification = formatPurchaseItemTimePeriodSpecification(purchaseItem, appContext);
    return purchaseItem.description + (timePeriodSpecification ? `, ${timePeriodSpecification}` : '');
};

export const formatPurchaseItemTimePeriodSpecification = (purchaseItem, appContext) => {
    let timePeriodSpecification = purchaseItem.timePeriodSpecification;
    if (!timePeriodSpecification && (purchaseItem.startDate || purchaseItem.endDate)) {
        const startDateString = formatShortDate(purchaseItem.startDate, appContext);
        const endDateString =  formatShortDate(purchaseItem.endDate, appContext);
        timePeriodSpecification = `${startDateString} - ${endDateString}`.trim();
    }
    return timePeriodSpecification;
};

export const createPerMonthPurchaseItemsAtDate = (bookingItems, date, appContext) => {
    const purchaseItems = [];
    const timePeriod = { startDate: date, endDate: date };

    const perMonthBookingItems = bookingItems
        .filter(o => o.paymentRecurrence === paymentRecurrences.perMonth.key &&
                    o.type !== bookingItemTypes.requestedService.key);

    perMonthBookingItems.forEach(perMonthBookingItem => {
        const overlapInfos = perMonthBookingItem.timePeriodPrices
            .map(tpp => getTimePeriodPriceOverlap(tpp, timePeriod, appContext))
            .filter(o => o.overlaps);

        overlapInfos.forEach(overlapInfo => {
            purchaseItems.push(createPurchaseItem(perMonthBookingItem, overlapInfo.timePeriodPrice, overlapInfo.timePeriodPrice.amount, perMonthBookingItem.description, undefined, undefined, appContext));
        });
    });

    return purchaseItems;
};

const createSubscriptionBookingPerMonthPurchaseItem = (perMonthBookingItem, timePeriodPrice, timePeriod, monthFactor, appContext) => {
    const amount = roundAmount(monthFactor * timePeriodPrice.amount, appContext);

    return {
        description: perMonthBookingItem.description,
        amount,
        vat: calculateVat(amount, timePeriodPrice.vatRate, appContext),
        currency: perMonthBookingItem.currency,
        vatRate: timePeriodPrice.vatRate,
        commissionRate: timePeriodPrice.commissionRate,
        type: perMonthBookingItem.type,
        referenceId: perMonthBookingItem.referenceId,
        startDate: timePeriod?.startDate,
        endDate: timePeriod?.endDate,
        bookingItem: perMonthBookingItem,
        bookingItemTimePeriodPrice: timePeriodPrice,
    };
};

const createPurchaseItem = (bookingItem, timePeriodPrice, amount, description, startDate, endDate, appContext) => ({
    amount,
    vat: calculateVat(amount, timePeriodPrice.vatRate, appContext),
    currency: bookingItem.currency,
    vatRate: timePeriodPrice.vatRate,
    description,
    commissionRate: timePeriodPrice.commissionRate,
    type: bookingItem.type,
    referenceId: bookingItem.referenceId,
    startDate,
    endDate,
    bookingItem,
    bookingItemTimePeriodPrice: timePeriodPrice
});
